import { IPoint } from '../struck';
import { StretchOptionType, type IImageTransform } from './image-layer';
import { TextVerticalAlign, type BaseTextInfo } from './text-layer';

export type EffectType = 'shadow' | 'reflection' | 'stroke';

export enum VectorType {
    /** 简单图形-矩形 */
    Rect = 'rect',
    /** 简单图形-椭圆形 */
    Ellipse = 'ellipse',
    /** 简单图形-多边形 */
    Polygon = 'polygon',
    /** 复杂图形 */
    Graph = 'graph',
}

/** 渐变色值 */
export interface ILinearGradient {
    /** 渐变点 */
    colors: {
        /** 颜色 */
        color: string;
        /** 偏移量 0.22 格式，百分比：0.22*100% */
        value: number;
    }[];
    /** 偏移角度 */
    angle: number;
    /** 渐变类型  线性 径向   0,1*/
    type?: number;
    point?: IPoint;
}

/**
 * 复杂图形-颜色列表
 */
export interface IGraphColors {
    /** 颜色 */
    color: string;
    /** 索引位置 */
    values: number[];
}

export interface IVectorEffectShadow {
    x: number;
    y: number;
    hidden: boolean;
    blur: number;
    color: string;
    opacity: number;
}

export interface IVectorEffectReflection extends Omit<IVectorEffectShadow, 'color' | 'x'> {
    size: number;
}
export interface IVectorEffect {
    /** 简单图形-阴影 */
    shadow: IVectorEffectShadow[];
    /** 简单图形-阴影 */
    reflection: IVectorEffectReflection[];
}

export interface IVectorBorderRadiusProps {
    leftTop: number;
    rightTop: number;
    leftBottom: number;
    rightBottom: number;
}

/**
 * 素材基础数据
 */
export interface IVectorMaterialInfo {
    /** 图形宽 */
    naturalWidth: number;
    /** 图形高 */
    naturalHeight: number;
    /** 图形类型 */
    vectorType: VectorType;
    /** 复杂图形-颜色列表 */
    colors: IGraphColors[];
    /** 简单图形-填充颜色 */
    fill: string;
    /** 简单图形-填充渐变色，只支持 linearGradient */
    fillGradient: ILinearGradient | null | '';
    /** 简单图形-矩形圆角 */
    radius: number;
    /** 简单图形-描边颜色 */
    stroke: string;
    /** 简单图形-描边渐变色，只支持 linearGradient */
    strokeGradient: ILinearGradient | null | '';
    /** 简单图形-描边宽度 */
    strokeWidth: number;
    /** 简单图形-描边类型 */
    strokeDasharray: string;
    /** 简单图形-描边形状 */
    strokeLinecap: string;
    /** 简单图形-描边是否隐藏 */
    strokeHidden: boolean;
    /** 简单图形-模糊 */
    blur: number;
    /** 简单图形-特效 */
    effect: IVectorEffect;
    /** 简单图形-矩形四个圆角 */
    borderRadius?: IVectorBorderRadiusProps;
    /** 简单图形-填充规则 */
    fillRule?: 'nonzero' | 'evenodd' | 'inherit' | undefined;
}

/**
 * 矢量图层文字内容内边距
 */
export type TextContentPadding = {
    top: number;
    right: number;
    bottom: number;
    left: number;
};

/**
 * 矢量图层文本信息 —— 可选字段
 */
export type VectorTextInfo = Omit<BaseTextInfo, 'textContents'> & {
    verticalAlign: TextVerticalAlign;
    padding?: TextContentPadding;
};

export interface IVectorLayerProps extends IVectorMaterialInfo {
    /** 素材地址 */
    url: string;
    /** 变换参数，裁剪，翻转 */
    imageTransform: IImageTransform;
    /** 渲染图层方式 */
    stretchOption: StretchOptionType;
    /** 取色器索引 */
    pickerIndex?: number | null;

    /**
     * 矢量图层文本信息，可选字段
     * 包含该字段则说明该矢量图层为文本可编辑
     */
    textInfo?: VectorTextInfo;
}
